'use client'

import { compactClassName } from '@/components/homepage-october/compact'
import { DestinationLink } from '../blocks/DestinationLink'

import * as React from 'react'
import { Calculator, Info, ListChecks, Maximize2, Ruler, Users } from '@/components/homepage-october/lucide'

import { Button } from '@/components/homepage-october/ui/button'
import { Card } from '@/components/homepage-october/ui/card'
import { cn } from '@/lib/utils'
import { ArrowRight } from '../blocks/icons'
import { Img } from '../blocks/Img'
import { ChipLinks, ChipRows, Eyebrow, Section, SectionHeader, SubHeader, words } from '../blocks/layout'
import { ProductCard } from '../blocks/ProductCard'
import { Marquee, ProductRail } from '../blocks/Rail'
import { useReducedMotion } from '../hooks'
import type { Link, Model } from '../types'

/* ------------------------------------------------------------------ shared bits */

const ctaClass = (k: number) =>
  cn('h-12! rounded-full! px-6! text-[15px]! font-semibold!', k > 0 && 'bg-white! hover:border-[#0469f0]! hover:bg-[#f3f8ff]! hover:text-[#0454c0]!')

/* ------------------------------------------------------------------ V08: what size do you need */

const SIZE_ICON = [Users, Ruler, Maximize2]
function SizeCard({ c, i, model }: { c: Model['size']['cards'][number]; i: number; model: Model }) {
  const widths = new Map(model.size.ladder.map((l) => [l.href, l]))
  const widthLinks = c.links.length > 0 && c.links.every((l) => widths.has(l.href))
  const I = SIZE_ICON[i % SIZE_ICON.length]
  return (
    <Card className={cn("v3-size-card", i === 1 && "v3-size-card-width")}>
      <div className={compactClassName("v3-size-intro")}><span><I aria-hidden="true" className="size-5" /></span><h3>{c.title}</h3></div>
      <p className={compactClassName("v3-size-copy")}>{c.text}</p>
      {c.kv.length ? <dl className={compactClassName("v3-capacities")}>{c.kv.map(([k, v]) => (
        <div key={k}><dt>{k}</dt><dd>{v}</dd></div>
      ))}</dl> : null}
      {widthLinks ? <ul className={compactClassName("v3-width-options")}>{c.links.map((l) => (
        <li key={l.href}><DestinationLink href={l.href} aria-label={l.aria || l.text}><span aria-hidden="true">{widths.get(l.href)?.label}</span><span className={compactClassName("sr-only")}>{l.text}</span></DestinationLink></li>
      ))}</ul> : null}
      {i === 2 ? <div className={compactClassName("v3-depth-diagram")} aria-label="Depth comparison: counter-depth 24 to 25 inches; standard-depth 30 to 36 inches">
        <div className={compactClassName("v3-depth-row")}><span>Counter-depth</span><strong>24–25″</strong><span aria-hidden="true" className={compactClassName("v3-depth-bar")} style={{ width: '69.4%' }} /></div>
        <div className={compactClassName("v3-depth-row")}><span>Standard-depth</span><strong>30–36″</strong><span aria-hidden="true" className={compactClassName("v3-depth-bar")} /></div>
      </div> : null}
      {!widthLinks && c.links.length ? <ChipLinks links={c.links} className="mt-4" /> : null}
      <DestinationLink href={c.more.href} aria-label={c.more.aria} className="v3-size-more">{words(c.more.text)}<ArrowRight aria-hidden="true" className="size-4 shrink-0" /></DestinationLink>
    </Card>
  )
}

/** Each width has a readable count and a link. Illustrations share a height so only width varies. */
function WidthLadder({ m }: { m: Model['size'] }) {
  const ref = React.useRef<HTMLUListElement>(null)
  const reduced = useReducedMotion()
  const [phase, setPhase] = React.useState<'static' | 'low' | 'grow'>('static')
  // Before the first paint of the live copy: if the row is off screen, shrink the fronts now and grow them when it
  // scrolls in. (Setting "low" in a frame callback raced the observer under load: "grow" could land first, the
  // fronts stayed full size and the entrance never played; the motion check caught it on 10/15/2026.)
  React.useLayoutEffect(() => {
    const el = ref.current
    if (!el || reduced || typeof IntersectionObserver === 'undefined') return
    const r = el.getBoundingClientRect()
    if (r.top < window.innerHeight && r.bottom > 0) return // already on screen: no entrance
    setPhase('low')
    const io = new IntersectionObserver(
      ([e]) => {
        if (!e.isIntersecting) return
        io.disconnect()
        setPhase('grow')
      },
      { threshold: 0.35 },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [reduced])
  const low = phase === 'low' && !reduced
  return (
    <div className={compactClassName("v3-width-ladder")}>
      <p><Ruler aria-hidden="true" className="size-4 text-[#0454c0]" />Choose a width to see matching models. Illustrations compare width only.</p>
      <ul ref={ref} aria-label={m.ladderAria} className={compactClassName("v3-width-grid")} data-low={low}>
        {m.ladder.map((l, i) => (
          <li key={l.href}>
            <DestinationLink href={l.href} className="v3-width-tile" aria-label={`${l.label} ${l.count} models, refrigerators by width`}>
              <span aria-hidden="true" className={compactClassName("v3-width-photo")}><span data-motion className={compactClassName("v3-width-front")} style={{ width: `${l.w * 1.7}px`, transitionDelay: phase === 'grow' ? `${i * 45}ms` : undefined }}><i /></span></span>
              <span className={compactClassName("v3-width-label")}>{l.label}</span>{' '}<span className={compactClassName("v3-width-count")}><b>{l.count}</b> models</span>
            </DestinationLink>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function Size({ model }: { model: Model }) {
  const m = model.size
  return (
    <Section aria={m.aria} v2="V08" tone="sky">
      <SectionHeader head={m.head} tone="sky" />
      <ul className={compactClassName("grid gap-4 lg:grid-cols-3 lg:gap-5")}>
        {m.cards.map((c, i) => (
          <li key={c.title}>
            <SizeCard c={c} i={i} model={model} />
          </li>
        ))}
      </ul>
      <SubHeader title={m.ladderTitle} link={m.ladderLink} className="mt-10" />
      <WidthLadder m={m} />
      <p className={compactClassName("mt-4 flex items-start gap-2 text-sm leading-relaxed text-neutral-600")}>
        <Info aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-[#0469f0]" />
        {m.note}
      </p>
      <div className={compactClassName("mt-8 flex flex-wrap gap-3")}>
        {m.ctas.map((c, k) => (
          <Button key={c.href} asChild size="lg" variant={k === 0 ? 'ctaPrimary' : 'outline'} className={ctaClass(k)}>
            <DestinationLink href={c.href} aria-label={c.aria}>
              {k === 0 ? <Calculator aria-hidden="true" /> : <ListChecks aria-hidden="true" />}
              {words(c.text)}
            </DestinationLink>
          </Button>
        ))}
      </div>
    </Section>
  )
}

/* ------------------------------------------------------------------ V08b: the exact refrigerator you need */

/** Width diagrams carry actual measurements; the other links use appliance photography. */
function NeedGrid({ links, visuals }: { links: Link[]; visuals: Model['exact']['visuals'] }) {
  const widths = links.filter((l) => /^\d+ Inch Wide/.test(l.text))
  const other = links.filter((l) => !widths.includes(l))
  return (
    <>
      {widths.length ? <ul className={compactClassName("v3-need-widths")}>
        {widths.map((l) => {
          const inches = Number.parseInt(l.text)
          return <li key={l.href}><DestinationLink href={l.href} className="v3-need-width">
            <span aria-hidden="true" className={compactClassName("v3-need-dimension")}>
              <svg viewBox="0 0 100 110" focusable="false">
                <rect x={(100 - inches * 1.5) / 2} y="10" width={inches * 1.5} height="78" rx="4" fill="#edf3fb" stroke="#7e9cc1" />
                <path d="M50 10v53M24 63h52M45 25v20M55 25v20M38 72h24" fill="none" stroke="#7e9cc1" strokeWidth="1.5" />
                <path d={`M${(100 - inches * 1.5) / 2} 96v10m0 -5h${inches * 1.5}m0 -5v10`} fill="none" stroke="#0469f0" strokeWidth="1.5" />
              </svg>
            </span>
            <span className={compactClassName("v3-need-width-copy")}><strong>{inches}″</strong>{' '}<span>Wide refrigerators</span></span>
            <ArrowRight aria-hidden="true" className="size-4 shrink-0" />
          </DestinationLink></li>
        })}
      </ul> : null}
      <ul className={compactClassName("v3-need-grid")}>
        {other.map((l) => {
          const visual = visuals[l.href]
          return <li key={l.href}>
            <Card className="v3-need-card">
              <DestinationLink href={l.href}>
                <span aria-hidden="true" className={compactClassName("v3-need-photo")}><Img src={visual.img.src} alt="" /></span>
                <span className={compactClassName("v3-need-copy")}>
                  <span className={compactClassName("v3-need-title")}>{l.text}</span>{' '}
                  <span className={compactClassName("v3-need-hint")}>{visual.swatch ? <i aria-hidden="true" style={{ background: visual.swatch }} /> : null}{visual.hint}</span>
                </span>
                <ArrowRight aria-hidden="true" className="v3-need-arrow" />
              </DestinationLink>
            </Card>
          </li>
        })}
      </ul>
    </>
  )
}

/** The "Fridges, Freezers, & Kitchen Stuff" ribbon. Repeats inside the first copy are hidden from assistive tech; the
 *  Marquee hides the loop copy, pauses on hover and focus, and stops for reduced motion. */
function Ribbon({ b }: { b: Model['exact']['banner'] }) {
  return (
    <div className={compactClassName("my-6 overflow-hidden rounded-2xl border border-[#e5eaf2] bg-white md:my-8")}>
      <Marquee label={b.aria} duration={38} className="py-3 md:py-3.5">
        {[0, 1, 2, 3].map((k) => (
          <span key={k} aria-hidden={k > 0 || undefined} className={compactClassName("flex shrink-0 items-center gap-8 pr-8 md:gap-10 md:pr-10")}>
            <span className={compactClassName("whitespace-nowrap text-xl font-bold tracking-tight text-[#0454c0] md:text-2xl")}>{b.text}</span>
            <img src={b.badge.src} alt={b.badge.alt} aria-hidden="true" loading="lazy" decoding="async" width={80} height={80} className={compactClassName("size-14 shrink-0 object-contain md:size-16")} />
          </span>
        ))}
      </Marquee>
    </div>
  )
}

export function Exact({ model }: { model: Model }) {
  const m = model.exact
  // The links grouped by what they are (owner 10/15/2026: the heading said refrigerator while the grid showed freezers
  // and coolers too; the heading now names all three). v2's ribbon runs after the refrigerators.
  const groups = m.groups ?? [{ label: 'Refrigerators', links: m.links }]
  return (
    <Section aria={m.aria} v2="V08b" tone="white">
      <SectionHeader head={{ title: m.title, viewAll: m.viewAll }} />
      {groups.map((g, k) => {
        return (
          <React.Fragment key={g.label}>
            {k === 1 ? <Ribbon b={m.banner} /> : null}
            <div className={compactClassName(cn(k > 1 && 'mt-5'))}>
              <h3 className={compactClassName("mb-2.5 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.12em] text-neutral-600")}>
                <span aria-hidden="true" className={compactClassName("size-1.5 rounded-full bg-[#ff9951]")} />
                {g.label}
              </h3>
              <NeedGrid links={g.links} visuals={m.visuals} />
            </div>
          </React.Fragment>
        )
      })}
      <ChipRows rows={m.budget} className="rounded-2xl border border-[#dce7f7] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04)] md:p-5" />
    </Section>
  )
}

/* ------------------------------------------------------------------ V09: best of 2026 */

export function Best({ model }: { model: Model }) {
  const m = model.best
  return (
    <Section aria={m.aria} v2="V09" tone="sand" className="overflow-hidden">
      <div aria-hidden="true" className={compactClassName("pointer-events-none absolute inset-0")}>
        <div className={compactClassName("absolute -top-40 right-[-120px] size-[560px] rounded-full bg-[radial-gradient(circle,rgba(255,153,81,0.18),transparent_65%)]")} />
        <div className={compactClassName("absolute bottom-[-180px] -left-40 size-[520px] rounded-full bg-[radial-gradient(circle,rgba(245,158,11,0.12),transparent_65%)]")} />
      </div>
      <div className={compactClassName("relative")}>
        <SectionHeader head={m.head} tone="sand" />
        {/* Refrigerator picks only (tools/extract_v3_model.py moves any other staff pick to its own specialty tab). */}
        <ul className={compactClassName("grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-4")}>
          {m.products.map((p) => (
            <li key={p.fin} className={compactClassName("transition duration-300 hover:-translate-y-1")}>
              <ProductCard p={p} className="ring-1 ring-[#ff9951]/15" />
            </li>
          ))}
        </ul>
        {/* On white, so the shared row label (neutral-500) keeps AA contrast; on the sand tint it would fall short. */}
        <ChipRows rows={m.chiprows} className="rounded-2xl border border-[#f0e4cf] bg-white p-4 md:p-5" />
      </div>
    </Section>
  )
}

/* ------------------------------------------------------------------ V09b: shop fridges */

export function Fridges({ model }: { model: Model }) {
  const m = model.fridges
  const b = m.band
  const r = m.rail
  const [eyebrow, title, ...subs] = b.lines
  const [textLink, ...buttons] = b.links
  return (
    <Section aria={m.aria} v2="V09b" tone="white">
      <Card className="gap-0! overflow-hidden rounded-3xl! border-[#ffe1cc]! py-0! shadow-[0_24px_60px_-34px_rgba(255,153,81,0.55)]! md:flex-row!">
        <div className={compactClassName("relative aspect-[3/2] md:aspect-auto md:min-h-[380px] md:w-1/2")}>
          <Img src={b.img.src} alt={b.img.alt} fit="cover" className="absolute inset-0 size-full" />
          <span aria-hidden="true" className={compactClassName("pointer-events-none absolute inset-0 bg-gradient-to-t from-white/10 to-transparent md:bg-gradient-to-l md:from-white/20")} />
        </div>
        <div className={compactClassName("relative flex flex-col justify-center overflow-hidden bg-gradient-to-br from-[#fff4ea] via-white to-[#eaf3ff] p-7 sm:p-10 md:w-1/2 lg:p-14")}>
          <div aria-hidden="true" className={compactClassName("pointer-events-none absolute -top-24 -right-24 size-72 rounded-full bg-[radial-gradient(circle,rgba(4,105,240,0.14),transparent_65%)]")} />
          <div className={compactClassName("relative")}>
            {eyebrow ? <Eyebrow tone="peach">{eyebrow}</Eyebrow> : null}
            {title ? (
              <p className={compactClassName("mt-3 text-balance bg-gradient-to-r from-[#0469f0] to-[#004aad] bg-clip-text text-4xl font-bold leading-[1.05] tracking-tight text-transparent md:text-5xl")}>{title}</p>
            ) : null}
            {subs.map((s) => (
              <p key={s} className={compactClassName("mt-4 max-w-md text-pretty text-lg leading-relaxed text-neutral-700")}>{s}</p>
            ))}
            <div className={compactClassName("mt-7 flex flex-col items-start gap-4")}>
              {textLink ? (
                <DestinationLink href={textLink.href} aria-label={textLink.aria} className="text-base font-semibold text-[#0454c0] underline decoration-[#0454c0]/30 underline-offset-4 transition hover:decoration-[#0454c0]">
                  {words(textLink.text)}
                </DestinationLink>
              ) : null}
              {buttons.map((l) => (
                <Button key={l.href} asChild size="lg" variant="ctaPrimary" className={ctaClass(0)}>
                  <DestinationLink href={l.href} aria-label={l.aria}>
                    {words(l.text)}
                    <ArrowRight aria-hidden="true" />
                  </DestinationLink>
                </Button>
              ))}
            </div>
          </div>
        </div>
      </Card>
      <div className={compactClassName("mt-10 md:mt-12")}>
        <ProductRail
          products={r.products}
          title={r.title}
          sub={r.sub}
          viewAll={r.viewAll}
          id={r.id}
          level={2}
          autoplay={6000}
        />
      </div>
    </Section>
  )
}
